import { useEffect, useState, type FormEvent } from 'react';
import { FileText } from 'lucide-react';
import { toast } from 'sonner';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { EmailEditor } from '@/components/newsletter/EmailEditor';
import {
  useCreateTemplate, useUpdateTemplate, type SaveTemplateInput,
} from '@/hooks/useNewsletter';
import {
  DEFAULT_THEME,
  type Template, type Block, type TemplateTheme,
} from '@/types/newsletter';

type Props = {
  open: boolean;
  onClose: () => void;
  template?: Template | null;
};

const EMPTY_BLOCKS: Block[] = [
  { id: crypto.randomUUID(), type: 'heading', content: { text: 'Your headline', level: 2, align: 'left' } },
  { id: crypto.randomUUID(), type: 'text',    content: { text: 'Your message here.', align: 'left' } },
  { id: crypto.randomUUID(), type: 'footer',  content: {
    companyName: 'Talbert Architectural Panels & Doors, Inc.',
    address: 'City of Industry, CA', fscLine: 'FSC® C014235',
  } },
];

export function TemplateFormModal({ open, onClose, template }: Props) {
  const create = useCreateTemplate();
  const update = useUpdateTemplate();
  const editing = !!template;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<Template['category']>('newsletter');
  const [locale, setLocale] = useState<'en' | 'es'>('en');
  const [subject, setSubject] = useState('');
  const [previewText, setPreviewText] = useState('');
  const [blocks, setBlocks] = useState<Block[]>(EMPTY_BLOCKS);
  const [theme, setTheme] = useState<TemplateTheme>(DEFAULT_THEME);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      if (template) {
        setName(template.name);
        setDescription(template.description ?? '');
        setCategory(template.category ?? 'newsletter');
        setLocale(template.locale ?? 'en');
        setSubject(template.subject ?? '');
        setPreviewText(template.previewText ?? '');
        setBlocks(template.blocks);
        setTheme(template.theme);
      } else {
        setName(''); setDescription(''); setCategory('newsletter'); setLocale('en');
        setSubject(''); setPreviewText('');
        setBlocks(EMPTY_BLOCKS); setTheme(DEFAULT_THEME);
      }
      setError(null);
    }
  }, [open, template]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!name.trim()) { setError('Template name is required.'); return; }

    const payload: SaveTemplateInput = {
      name, description, category, locale, subject, previewText, blocks, theme,
    };
    try {
      if (editing && template) {
        await update.mutateAsync({ id: template.id, ...payload });
        toast.success(`Updated ${name}`);
      } else {
        await create.mutateAsync(payload);
        toast.success(`Created ${name}`);
      }
      onClose();
    } catch (err) {
      setError((err as { message?: string }).message ?? 'Save failed.');
    }
  }

  const submitting = create.isPending || update.isPending;

  return (
    <Modal
      open={open}
      onClose={onClose}
      kicker={editing ? 'Edit Template' : 'New Template'}
      title={editing ? (template?.name ?? '') : 'Create a reusable template.'}
      description="Templates let you reuse a design across many campaigns. Campaigns made from templates get their own copy — edits to the template don't change sent campaigns."
      size="xl"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button type="submit" form="tpl-form" loading={submitting} leftIcon={<FileText size={13} />}>
            {editing ? 'Save Changes' : 'Create Template'}
          </Button>
        </>
      }
    >
      <form id="tpl-form" onSubmit={onSubmit} className="space-y-5 py-2">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input label="Name" required placeholder="Monthly newsletter"
            value={name} onChange={(e) => setName(e.target.value)} />
          <Select label="Category"
            value={category ?? 'newsletter'}
            onChange={(e) => setCategory(e.target.value as Template['category'])}
            options={[
              { value: 'newsletter',    label: 'Newsletter' },
              { value: 'announcement',  label: 'Announcement' },
              { value: 'greeting',      label: 'Greeting / Holiday' },
              { value: 'promotion',     label: 'Promotion' },
              { value: 'transactional', label: 'Transactional' },
            ]}
          />
        </div>

        <Textarea label="Description" rows={2} value={description}
          onChange={(e) => setDescription(e.target.value)} />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Input label="Default Subject" placeholder="Optional default subject line"
            value={subject} onChange={(e) => setSubject(e.target.value)} />
          <Input label="Preview Text" placeholder="Inbox preview"
            value={previewText} onChange={(e) => setPreviewText(e.target.value)} />
          <Select label="Language"
            value={locale}
            onChange={(e) => setLocale(e.target.value as 'en' | 'es')}
            options={[
              { value: 'en', label: 'English' },
              { value: 'es', label: 'Español' },
            ]}
          />
        </div>

        <div className="pt-4 border-t border-border">
          <div className="kicker mb-3">Design</div>
          <EmailEditor
            blocks={blocks}
            theme={theme}
            onBlocksChange={setBlocks}
            onThemeChange={setTheme}
          />
        </div>

        {error && <div className="text-xs text-danger border-l-2 border-danger pl-3 py-1">{error}</div>}
      </form>
    </Modal>
  );
}
